import { useEffect, useState } from 'react'
import axios from 'axios'
import { apiUrl } from '../config'
import { time2seconds } from './Time'

/**
 * Our API fetches the weather from Open-Meteo and keeps it, so visitors don't call Open-Meteo
 * themselves: past days for good, today with its forecast for a quarter of an hour
 * @type {string}
 */
const WEATHER_REQUEST = 'weather'

/**
 * How long the weather of today is kept before it is fetched again, in ms; as long as our API
 * keeps it
 * @type {number}
 */
const TODAY_REFRESH = 15 * 60 * 1000

/**
 * Seconds each weather value covers: the quarter of an hour before its time
 * @type {number}
 */
const SLOT = 15 * 60

/**
 * Shortest dry spell in slots that counts as a change between sun and clouds, so the
 * weather doesn't flip back and forth every quarter of an hour
 * @type {number}
 */
const MIN_DRY_SLOTS = 2

/**
 * Gusts in km/h from which the wind counts as strong: wind force 7, where walking against it
 * takes effort and people need to keep their balance
 * @type {number}
 */
const WINDY_GUSTS = 50

/**
 * Gusts in km/h below which strong wind counts as over; lower than WINDY_GUSTS so wind around
 * the limit doesn't switch on and off every quarter of an hour
 * @type {number}
 */
const CALM_GUSTS = 40

/**
 * Lowest wind speed in km/h of each Beaufort wind force from 1 up
 * @type {Array<number>}
 */
const BEAUFORT = [ 1, 6, 12, 20, 29, 39, 50, 62, 75, 89, 103, 118 ]

/**
 * Compass points the wind can come from, clockwise from north
 * @type {Array<string>}
 */
const COMPASS = [ 'N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW' ]

/**
 * Credit for the weather data: its CC BY 4.0 licence asks for this link next to the data, and a link to the licence
 * @type {{ label: string, url: string, licence: string, licenceUrl: string }}
 */
export const WEATHER_CREDIT = {
  label: 'Weather data by Open-Meteo.com',
  url: 'https://open-meteo.com/',
  licence: 'CC BY 4.0',
  licenceUrl: 'https://creativecommons.org/licenses/by/4.0/'
}

/**
 * Weather conditions that matter for grip, with their label, whether the track gets wet and
 * whether there is one icon for it with strong wind; the icons are icon-weather-<key> and
 * icon-weather-<key>-windy
 * @type {Object<string, { label: string, wet: boolean, windIcon: boolean }>}
 */
export const CONDITIONS = {
  sun: { label: 'Sunny', wet: false, windIcon: true },
  partly: { label: 'Partly cloudy', wet: false, windIcon: true },
  clouds: { label: 'Overcast', wet: false, windIcon: true },
  fog: { label: 'Fog', wet: false, windIcon: false },
  drizzle: { label: 'Drizzle', wet: true, windIcon: true },
  rain: { label: 'Rain', wet: true, windIcon: true },
  snow: { label: 'Snow', wet: true, windIcon: true },
  hail: { label: 'Hail', wet: true, windIcon: false },
  thunder: { label: 'Thunderstorm', wet: true, windIcon: false }
}

/**
 * Fetched weather per day, with the time it was fetched
 * @type {Map<string, { at: number, request: Promise<Object|null> }>}
 */
const cache = new Map()

/**
 * Minutes ahead the forecast looks
 * @type {number}
 */
export const FORECAST_MINUTES = 60

/**
 * Get today as YYYY-MM-DD in local time
 * @returns {string} date
 */
const getToday = () => new Date().toLocaleDateString( 'sv-SE' )

/**
 * Check whether a day is today, the only day with a forecast
 * @param {string} date day as YYYY-MM-DD
 * @returns {boolean} whether it is today
 */
export const isToday = ( date ) => date !== '' && date === getToday()

/**
 * Get the time of day now
 * @returns {number} seconds since midnight in local time
 */
export const getSecondsNow = () =>
{
  const now = new Date()
  return now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds()
}

/**
 * Get the day of the loaded laps
 * @param {Object|null} contest contest of the loaded laps
 * @returns {string} day as YYYY-MM-DD, empty without a contest
 */
export const getContestDay = ( contest ) => contest ? contest.day || contest.date || '' : ''

/**
 * Get the icons of the weather: one icon for the weather with strong wind where there is one,
 * otherwise the weather and a wind icon
 * @param {string} condition key in CONDITIONS
 * @param {boolean} windy whether the wind is strong
 * @returns {Array<string>} icon names
 */
export const getWeatherIcons = ( condition, windy ) =>
{
  if ( !windy ) return [ `weather-${ condition }` ]
  return CONDITIONS[ condition ].windIcon ? [ `weather-${ condition }-windy` ] : [ `weather-${ condition }`, 'weather-wind' ]
}

/**
 * Get the condition of a WMO weather code
 * @param {number} code WMO weather code
 * @returns {string} key in CONDITIONS
 */
const getCondition = ( code ) =>
{
  if ( code <= 1 ) return 'sun'
  if ( code === 2 ) return 'partly'
  if ( code === 3 ) return 'clouds'
  if ( code <= 48 ) return 'fog'
  if ( code <= 57 ) return 'drizzle'
  if ( code <= 67 || ( code >= 80 && code <= 82 ) ) return 'rain'
  if ( code <= 86 ) return 'snow'
  if ( code === 95 ) return 'thunder'
  return 'hail'
}

/**
 * Check whether two periods have the same weather
 * @param {Object} a period with condition and windy
 * @param {Object} b period with condition and windy
 * @returns {boolean} whether condition and strong wind are the same
 */
const isSameWeather = ( a, b ) => a.condition === b.condition && a.windy === b.windy

/**
 * Join the weather into periods of one condition and wind; a short spell of sun or clouds
 * between the same weather is left out
 * @param {Array<number>} ends end of each slot in seconds since midnight
 * @param {Array<number>} codes WMO weather code of each slot
 * @param {Array<boolean>} windy whether the wind is strong in each slot
 * @returns {Array<{ start: number, end: number, condition: string, windy: boolean }>} periods, in time order
 */
const getPeriods = ( ends, codes, windy ) =>
{
  const periods = []

  codes.forEach( ( code, index ) =>
  {
    const slot = { start: ends[ index ] - SLOT, end: ends[ index ], condition: getCondition( code ), windy: windy[ index ] }
    const last = periods[ periods.length - 1 ]

    if ( last && isSameWeather( last, slot ) )
    {
      last.end = slot.end
      return
    }

    periods.push( slot )
  } )

  // fold short dry spells back into the period before them when the same weather follows
  return periods.reduce( ( merged, period, index ) =>
  {
    const before = merged[ merged.length - 1 ]
    const after = periods[ index + 1 ]
    const short = !CONDITIONS[ period.condition ].wet && period.end - period.start < MIN_DRY_SLOTS * SLOT

    if ( before && ( isSameWeather( before, period ) || ( short && before.windy === period.windy && after && isSameWeather( after, before ) ) ) )
    {
      before.end = period.end
      return merged
    }

    merged.push( { ...period } )
    return merged
  }, [] )
}

/**
 * Mark the slots with strong wind: from gusts of WINDY_GUSTS until they drop below CALM_GUSTS
 * @param {Array<number>} gusts gusts in km/h of each slot
 * @returns {Array<boolean>} whether the wind is strong in each slot
 */
const getWindy = ( gusts ) =>
{
  let windy = false
  return gusts.map( gust =>
  {
    windy = windy ? gust >= CALM_GUSTS : gust >= WINDY_GUSTS
    return windy
  } )
}

/**
 * Get the Beaufort wind force of a wind speed
 * @param {number} speed wind speed in km/h
 * @returns {number} wind force from 0 to 12
 */
const getBeaufort = ( speed ) => BEAUFORT.filter( limit => speed >= limit ).length

/**
 * Get the compass point the wind comes from
 * @param {number} degrees wind direction in degrees, 0 for north
 * @returns {string} compass point like SW
 */
const getCompass = ( degrees ) => COMPASS[ Math.round( ( ( degrees % 360 ) + 360 ) % 360 / 45 ) % COMPASS.length ]

/**
 * Fetch the weather of a day at the circuit per quarter of an hour, once per day; today includes
 * the forecast for the quarters still to come
 * @param {string} date day as YYYY-MM-DD
 * @returns {Promise<Object|null>} weather with the end of each slot in seconds, temperature in °C,
 *   precipitation in mm, wind and gusts in km/h, wind direction in degrees and the periods of each
 *   condition and strong wind; null on failure
 */
const fetchWeather = ( date ) =>
{
  const cached = cache.get( date )
  if ( cached && ( date !== getToday() || Date.now() - cached.at < TODAY_REFRESH ) ) return cached.request

  const request = axios.get( apiUrl, { params: { [ WEATHER_REQUEST ]: date } } )
    .then( ( { data } ) =>
    {
      const slots = data && data.request === WEATHER_REQUEST ? data.data : null
      if ( !slots || !Array.isArray( slots.time ) ) return null

      // keep the slots that have values, such as the past ones today
      const ends = [],
            temperature = [],
            precipitation = [],
            codes = [],
            wind = [],
            gusts = [],
            direction = []

      slots.time.forEach( ( time, index ) =>
      {
        if ( typeof slots.temperature_2m[ index ] !== 'number' || typeof slots.weather_code[ index ] !== 'number' ) return
        ends.push( time2seconds( time.split( 'T' )[ 1 ] ) )
        temperature.push( slots.temperature_2m[ index ] )
        precipitation.push( slots.precipitation[ index ] || 0 )
        codes.push( slots.weather_code[ index ] )
        wind.push( slots.wind_speed_10m[ index ] || 0 )
        gusts.push( slots.wind_gusts_10m[ index ] || 0 )
        direction.push( slots.wind_direction_10m[ index ] || 0 )
      } )

      return ends.length ? { ends, temperature, precipitation, wind, gusts, direction, periods: getPeriods( ends, codes, getWindy( gusts ) ) } : null
    } )
    .catch( () =>
    {
      // try again next time
      cache.delete( date )
      return null
    } )

  cache.set( date, { at: Date.now(), request } )
  return request
}

/**
 * Get the weather at a time of day: the temperature between the slots around it, the
 * precipitation and wind of the quarter it falls in, the condition and whether the wind is strong
 * @param {Object|null} weather weather from useWeather
 * @param {number} seconds seconds since midnight
 * @returns {Object|null} temperature in °C, precipitation in mm per quarter, condition key, windy, and wind
 *   with its Beaufort force, compass point and gusts in km/h; null when unknown
 */
export const getWeatherAt = ( weather, seconds ) =>
{
  if ( !weather ) return null

  const { ends, temperature, precipitation, wind, gusts, direction, periods } = weather
  const index = ends.findIndex( end => end >= seconds )
  if ( index < 0 ) return null

  const before = index > 0 ? index - 1 : index
  const part = ends[ index ] > ends[ before ] ? ( seconds - ends[ before ] ) / ( ends[ index ] - ends[ before ] ) : 0
  const period = periods.find( p => p.start <= seconds && seconds < p.end ) || periods[ periods.length - 1 ]

  return {
    temperature: temperature[ before ] + ( temperature[ index ] - temperature[ before ] ) * Math.min( Math.max( part, 0 ), 1 ),
    precipitation: precipitation[ index ],
    condition: period.condition,
    windy: period.windy,
    wind: { beaufort: getBeaufort( wind[ index ] ), from: getCompass( direction[ index ] ), gusts: Math.round( gusts[ index ] ) }
  }
}

/**
 * Get the condition at the start of a time range and every change of condition within it
 * @param {Object|null} weather weather from useWeather
 * @param {number} from start of the range in seconds since midnight
 * @param {number} to end of the range in seconds since midnight
 * @returns {Array<{ seconds: number, condition: string, windy: boolean, change: boolean }>} the start and the changes, in time order
 */
export const getWeatherChanges = ( weather, from, to ) =>
{
  if ( !weather ) return []

  return weather.periods
    .filter( period => period.end > from && period.start <= to )
    .map( period => ( { seconds: Math.max( period.start, from ), condition: period.condition, windy: period.windy, change: period.start > from } ) )
}

/**
 * Get the weather now and the first change in the coming minutes, as far as the weather is known;
 * a change between dry and rain, snow or hail is a big change
 * @param {Object|null} weather weather from useWeather, of today
 * @param {number} now seconds since midnight
 * @param {number} minutes minutes to look ahead
 * @returns {Object|null} condition, windy and temperature now, the minutes looked ahead, and the
 *   change with its condition, windy, minutes from now and whether it is big; null when unknown
 */
export const getForecast = ( weather, now, minutes = FORECAST_MINUTES ) =>
{
  if ( !weather || !weather.ends.length ) return null

  const until = Math.min( now + minutes * 60, weather.ends[ weather.ends.length - 1 ] )
  const ahead = Math.round( ( until - now ) / 60 )
  const changes = getWeatherChanges( weather, now, until )
  const at = getWeatherAt( weather, now )
  if ( ahead < 1 || !changes.length || !at ) return null

  const current = changes[ 0 ]
  const next = changes.find( change => change.change )

  return {
    condition: current.condition,
    windy: current.windy,
    temperature: at.temperature,
    minutes: ahead,
    change: !next ? null : {
      condition: next.condition,
      windy: next.windy,
      minutes: Math.max( 1, Math.round( ( next.seconds - now ) / 60 ) ),
      big: CONDITIONS[ next.condition ].wet !== CONDITIONS[ current.condition ].wet
    }
  }
}

/**
 * Say what the forecast expects: the first change and when, or the weather staying the same
 * @param {Object} forecast forecast from getForecast
 * @returns {string} text like 'Rain in 10 min' or 'Overcast for the next 60 min'
 */
export const describeForecast = ( forecast ) =>
{
  const { change } = forecast
  const now = CONDITIONS[ forecast.condition ]
  if ( !change ) return `${ now.label } for the next ${ forecast.minutes } min`

  const next = CONDITIONS[ change.condition ]
  const parts = []
  if ( change.condition !== forecast.condition )
  {
    parts.push( now.wet && !next.wet ? 'dry' : next.label.toLowerCase() )
  }
  if ( change.windy !== forecast.windy )
  {
    parts.push( change.windy ? 'strong wind' : 'less wind' )
  }

  const text = `${ parts.join( ', ' ) } in ${ change.minutes } min`
  return text.charAt( 0 ).toUpperCase() + text.slice( 1 )
}

/**
 * Get the weather at the circuit on a day; today is fetched again every quarter of an hour
 * @param {string} date day as YYYY-MM-DD, empty for none
 * @returns {Object|null} weather, null while loading or without a day
 */
export const useWeather = ( date = '' ) =>
{
  const [ weather, setWeather ] = useState( null )

  useEffect( () =>
  {
    setWeather( null )
    if ( !date ) return

    let active = true
    const load = () => fetchWeather( date ).then( result => active && result && setWeather( result ) )
    load()

    const timer = date === getToday() ? setInterval( load, TODAY_REFRESH ) : null

    return () =>
    {
      active = false
      if ( timer ) clearInterval( timer )
    }
  }, [ date ] )

  return weather
}
